import { useAppStore } from '@/store';
import type { AppView, StepStatus } from '@/types';

const STEPS: { view: AppView; label: string }[] = [
  { view: 'import',   label: '1 · Import'    },
  { view: 'diagnose', label: '2 · Diagnose'  },
  { view: 'domain',   label: '3 · Domain Ω'  },
  { view: 'build',    label: '4 · Build LUT' },
  { view: 'export',   label: '5 · Export'    },
  { view: 'apply',    label: '6 · Apply'     },
];

/** A step is reachable if it or any earlier step has been started/completed. */
function isReachable(status: StepStatus): boolean {
  return status !== 'idle';
}

function stepIcon(status: StepStatus, isCurrent: boolean): string {
  if (status === 'done')    return '✓ ';
  if (status === 'error')   return '✕ ';
  if (status === 'running') return '⟳ ';
  if (isCurrent)            return '● ';
  return '';
}

export default function NavBar() {
  const currentView  = useAppStore(s => s.currentView);
  const stepStatus   = useAppStore(s => s.session.stepStatus);
  const setView      = useAppStore(s => s.setView);
  const resetSession = useAppStore(s => s.resetSession);

  return (
    <header className="flex items-center gap-1 px-4 py-2 bg-slate-800
                       border-b border-slate-700 shrink-0">
      <span className="font-mono text-sky-400 font-bold text-sm mr-4 whitespace-nowrap">
        RGB Cal
      </span>

      <nav className="flex items-center gap-0.5 flex-1">
        {STEPS.map(({ view, label }, idx) => {
          const status    = stepStatus[view];
          const isCurrent = view === currentView;
          const reachable = isCurrent || isReachable(status);

          // Connector arrow between steps
          const connector = idx > 0
            ? <span key={'sep-' + idx} className="text-slate-600 text-xs select-none mx-0.5">›</span>
            : null;

          const btn = (
            <button
              key={view}
              disabled={!reachable}
              onClick={() => reachable && setView(view)}
              title={!reachable ? 'Complete the previous step first' : undefined}
              className={[
                'px-3 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap',
                !reachable
                  ? 'text-slate-600 cursor-not-allowed'
                  : isCurrent
                    ? 'bg-slate-700 text-sky-300 ring-1 ring-sky-600'
                    : status === 'done'
                      ? 'text-emerald-400 hover:bg-slate-700/50'
                      : status === 'error'
                        ? 'text-red-400 hover:bg-slate-700/50'
                        : status === 'running'
                          ? 'text-amber-400 animate-pulse'
                          : 'text-sky-400 hover:bg-slate-700/50',
              ].join(' ')}
            >
              {stepIcon(status, isCurrent)}{label}
            </button>
          );

          return connector ? [connector, btn] : [btn];
        })}
      </nav>

      <button
        onClick={resetSession}
        className="ml-4 text-xs text-slate-500 hover:text-red-400 transition-colors px-2 py-1 rounded"
        title="Reset session and start over"
      >
        ✕ Reset
      </button>
    </header>
  );
}
